import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from 'react'
import { login as loginRequest, logout as logoutRequest, type StaffProfile } from './api'
import {
  clearSession,
  decodeJwt,
  getClaims,
  isCrossClubRole,
  isExpired,
  saveSession,
  setMemToken,
  type StaffClaims,
  type StaffRole
} from './storage'
import { setUnauthorizedHandler } from '@/api/client'

interface AuthState {
  /** профиль сотрудника, если залогинен */
  staff: StaffProfile | null
  /** декодированные claims из JWT (sub, club_id, role) */
  claims: StaffClaims | null
  isAuthenticated: boolean
  isSuperAdmin: boolean
  /** Роль без своего клуба (super_admin/support/manager) — клуб выбирается в шапке. */
  isCrossClub: boolean
  login: (email: string, password: string) => Promise<void>
  /** Вход по готовой сессии из Help (SSO-обмен кода) — см. pages/Sso.tsx. */
  loginWithSession: (token: string, profile: StaffProfile) => void
  logout: () => void
  /** проверка роли для UI-гейтов */
  hasRole: (...roles: StaffRole[]) => boolean
}

const AuthContext = createContext<AuthState | null>(null)

const STAFF_KEY = 'bo.auth.staff'

function loadStaff(): StaffProfile | null {
  try {
    const raw = localStorage.getItem(STAFF_KEY)
    return raw ? (JSON.parse(raw) as StaffProfile) : null
  } catch {
    return null
  }
}

export function AuthProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [claims, setClaims] = useState<StaffClaims | null>(() => {
    const c = getClaims()
    // протухший токен → не считаем залогиненным
    return c && !isExpired(c) ? c : null
  })
  const [staff, setStaff] = useState<StaffProfile | null>(() => {
    const c = getClaims()
    return c && !isExpired(c) ? loadStaff() : null
  })

  const logout = useCallback(() => {
    // Гасим httpOnly-cookie на сервере (best-effort) + чистим локальные claims/профиль.
    void logoutRequest().catch(() => undefined)
    clearSession()
    localStorage.removeItem(STAFF_KEY)
    setClaims(null)
    setStaff(null)
  }, [])

  // Клиент дёргает этот колбэк при 401 → синхронно чистим контекст.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      localStorage.removeItem(STAFF_KEY)
      setClaims(null)
      setStaff(null)
    })
    return () => setUnauthorizedHandler(null)
  }, [])

  // Если токен протух при загрузке — подчищаем хранилище.
  useEffect(() => {
    const c = getClaims()
    if (c && isExpired(c)) logout()
  }, [logout])

  // Общая часть login() и SSO-обмена: и обычный вход, и /sso получают от бэкенда
  // тот же { token, staff } — разница только в том, как токен добыт.
  const applySession = useCallback((token: string, profile: StaffProfile) => {
    const decoded = decodeJwt(token)
    const nextClaims: StaffClaims = decoded ?? {
      sub: profile.id,
      club_id: profile.clubId,
      role: profile.role,
      email: profile.email
    }
    // Токен НЕ кладём в localStorage — основной канал httpOnly-cookie от сервера;
    // в память (Bearer-fallback на время сессии, если cookie заблокирована браузером).
    setMemToken(token)
    saveSession(nextClaims)
    localStorage.setItem(STAFF_KEY, JSON.stringify(profile))
    setClaims(nextClaims)
    setStaff(profile)
  }, [])

  const login = useCallback(
    async (email: string, password: string) => {
      const { token, staff: profile } = await loginRequest({ email, password })
      applySession(token, profile)
    },
    [applySession]
  )

  // Используется страницей /sso (см. pages/Sso.tsx) после обмена одноразового
  // кода из Help на { token, staff } через POST /v1/auth/sso-exchange.
  const loginWithSession = useCallback(
    (token: string, profile: StaffProfile) => applySession(token, profile),
    [applySession]
  )

  const value = useMemo<AuthState>(() => {
    const role = claims?.role ?? null
    return {
      staff,
      claims,
      isAuthenticated: Boolean(claims),
      isSuperAdmin: role === 'super_admin',
      isCrossClub: isCrossClubRole(role),
      login,
      loginWithSession,
      logout,
      hasRole: (...roles) => (role ? roles.includes(role) : false)
    }
  }, [staff, claims, login, loginWithSession, logout])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth должен вызываться внутри <AuthProvider>')
  return ctx
}
